// SparklineSVG.jsx — v1.0.2 Item 2
//
// Tiny presentational sparkline. Pure SVG, no deps. Takes an array of numeric
// points and renders a smooth path scaled to fit the box.
//
// Usage:
//   <SparklineSVG points={[0.42, 0.51, 0.39, 0.62, 0.58]} />
//
// Behavior:
//   • <2 points → renders a dimmed dash so layout stays consistent.
//   • All-equal points → renders a flat line at vertical center.
//   • Color follows the slope (last vs first): green up, red down, slate flat.
import React from "react";

const W = 60;
const H = 16;
const PAD = 1;

export default function SparklineSVG({ points = [], testId }) {
  if (!Array.isArray(points) || points.length < 2) {
    return (
      <svg
        width={W}
        height={H}
        className="text-slate-200"
        data-testid={testId || "sparkline-empty"}
        aria-hidden="true"
      >
        <line
          x1={PAD}
          y1={H / 2}
          x2={W - PAD}
          y2={H / 2}
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="2 2"
        />
      </svg>
    );
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = (W - PAD * 2) / (points.length - 1);

  // Map points to coordinates. Higher value → smaller y (top of svg).
  const coords = points.map((v, i) => {
    const x = PAD + i * stepX;
    const y = PAD + (H - PAD * 2) * (1 - (v - min) / range);
    return [x, y];
  });

  const path = coords
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");

  const first = points[0];
  const last = points[points.length - 1];
  const slope = last - first;
  const stroke =
    Math.abs(slope) < 1e-6
      ? "#94a3b8" // slate-400 flat
      : slope > 0
      ? "#10b981" // emerald-500 up
      : "#ef4444"; // red-500 down

  return (
    <svg
      width={W}
      height={H}
      className="overflow-visible"
      data-testid={testId || "sparkline"}
      aria-hidden="true"
    >
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End-point dot for visual anchoring */}
      <circle
        cx={coords[coords.length - 1][0]}
        cy={coords[coords.length - 1][1]}
        r="1.5"
        fill={stroke}
      />
    </svg>
  );
}
